import NameCellWithImage, { ChipCell } from 'components/controls/image-cell';
import { useWorkspace } from 'hooks/useWorkspace';
import { MRT_ColumnDef } from 'material-react-table';
import { useTranslation } from 'react-i18next';
import {
    NESTED_PRODUCT_CATEGORY_IS_MULIT_COLOR,
    NESTED_PRODUCT_CATEGORY_STATUS_COLOR,
    useNestedProductCategoryStatus,
    useNestedProductIsMultiSelect,
} from './status';
import { useMemo } from 'react';
import { ToolTip } from 'components/controls';

export type NestedProductCategoriesColumns = {
    id: string;
    name: string;
    isActive: string;
};

export const useNestedProductCategoryTableColumns = (): MRT_ColumnDef<NestedProductCategoriesColumns>[] => {
    const { t } = useTranslation();
    const { baseWorkspaceUrl } = useWorkspace();
    const statuses = useNestedProductCategoryStatus();
    const multiSelects = useNestedProductIsMultiSelect();

    return useMemo(
        () => [
            {
                accessorKey: 'name',
                header: t('TABLE_HEADERS.NAME'),
                enableColumnActions: false,
                Cell: ({ cell }) => {
                    const path = `${baseWorkspaceUrl}/nestedProductCategory/${cell.row.original.id}`;
                    return (
                        <ToolTip
                            disableHoverListener={cell.row.original['name'].length < 20}
                            title={cell.getValue<string>()}
                            placement="top-end"
                        >
                            <NameCellWithImage
                                cell={cell}
                                isLink
                                path={path}
                                target="_self"
                                defaultImage={'https://cdn-icons-png.freepik.com/512/7997/7997145.png'}
                            />
                        </ToolTip>
                    );
                },
                size: 220,
            },
            {
                accessorKey: 'isActive',
                header: t('TABLE_HEADERS.STATUS'),
                filterVariant: 'autocomplete',
                enableSorting: false,
                enableColumnActions: false,
                size: 100,
                Cell: ({ cell }) => {
                    const status = cell.getValue() ? statuses.ACTIVE : statuses.INACTIVE;

                    return (
                        <ChipCell
                            value={cell.getValue<string>() ? 'ACTIVE' : 'INACTIVE'}
                            label={status}
                            statusColorMap={NESTED_PRODUCT_CATEGORY_STATUS_COLOR}
                        />
                    );
                },
            },
            {
                accessorKey: 'isMultiSelect',
                header: t('TABLE_HEADERS.MULTI_SELECT'),
                enableColumnActions: false,
                enableColumnFilter: false,
                size: 100,
                Cell: ({ cell }) => {
                    const status = cell.getValue() ? multiSelects.YES : multiSelects.NO;

                    return (
                        <ChipCell
                            value={cell.getValue<string>() ? 'YES' : 'NO'}
                            label={status}
                            statusColorMap={NESTED_PRODUCT_CATEGORY_IS_MULIT_COLOR}
                        />
                    );
                },
            },
            {
                accessorKey: 'maxSelection',
                header: t('TABLE_HEADERS.MAX_SELECTIONS'),
                enableColumnActions: false,
                enableColumnFilter: false,
                Cell: ({ cell }) => {
                    const value = cell.getValue<string>();
                    if (value === 'UNLIMITED') {
                        return t('COMMON.UNLIMITED');
                    } else {
                        return value;
                    }
                },
                size: 150,
            },
            {
                accessorKey: 'createdAt',
                header: t('TABLE_HEADERS.CREATION_DATE'),
                enableColumnActions: false,
                enableColumnFilter: false,
                size: 100,
            },
        ],
        [t, baseWorkspaceUrl, multiSelects, statuses],
    );
};
